import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Plus, Mail, Phone, Building2, MapPin, Pencil, Trash2, UserCheck, Calendar, Clock, Settings2, X, Tag, Archive, RotateCcw, UserMinus, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeesPage = () => {
  const { user, isHR } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    name: '', email: '', phone: '', personal_email: '', department: '', designation: '',
    pilot_id: '', years_of_experience: '', date_of_joining: '', tech_stack: '',
    employee_id: '', employment_type: 'full_time', contract_duration_months: '12', contract_end_date: '',
    team_kernel_id: '', team_kernel_layer_idx: '',
    // Login account fields
    create_login: true, role: 'employee', initial_password: '',
  });
  const [onboardingProof, setOnboardingProof] = useState(null);
  const [onboardingProofPreview, setOnboardingProofPreview] = useState(null);
  const [nextCodeHint, setNextCodeHint] = useState('');

  // Team Kernels — for the auto-add dropdown on the create form
  const [teamKernels, setTeamKernels] = useState([]);
  // Onboarding-proof management for existing employees (Edit dialog)
  const [editProofFile, setEditProofFile] = useState(null);
  const [editProofMeta, setEditProofMeta] = useState(null);
  const [editProofBusy, setEditProofBusy] = useState(false);

  // Tech Stack Management
  const [isTechStackDialogOpen, setIsTechStackDialogOpen] = useState(false);
  const [masterTechStacks, setMasterTechStacks] = useState([]);
  const [newTechName, setNewTechName] = useState('');
  const [newTechCategory, setNewTechCategory] = useState('');
  const [isTechMappingOpen, setIsTechMappingOpen] = useState(false);
  const [mappingEmployee, setMappingEmployee] = useState(null);
  const [selectedTechs, setSelectedTechs] = useState([]);

  // Offboarding handled by Exit Management page — opens via navigate() in openOffboardDialog

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchMasterTechStacks();
    // Fetch team kernels so HR can pick one during onboarding
    axios.get(`${API_URL}/api/team-kernels`, { withCredentials: true })
      .then(res => setTeamKernels(res.data || []))
      .catch(() => setTeamKernels([]));
  }, []);

  // Prefetch the next auto-generated employee code whenever the employment_type changes.
  useEffect(() => {
    if (!isAddDialogOpen) return;
    axios.get(`${API_URL}/api/employees/next-code?employment_type=${encodeURIComponent(newEmployee.employment_type || 'full_time')}`, { withCredentials: true })
      .then(res => setNextCodeHint(res.data?.next_code || ''))
      .catch(() => setNextCodeHint(''));
  }, [newEmployee.employment_type, isAddDialogOpen]);

  const onProofChange = (e) => {
    const f = e.target.files?.[0] || null;
    setOnboardingProof(f);
    if (f) {
      if (!f.type.startsWith('image/')) {
        toast.error('Onboarding proof must be an image (JPEG / PNG / WebP / HEIC)');
        setOnboardingProof(null);
        setOnboardingProofPreview(null);
        e.target.value = '';
        return;
      }
      const url = URL.createObjectURL(f);
      setOnboardingProofPreview(url);
    } else {
      setOnboardingProofPreview(null);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Fetch ALL (including archived/relieved) so the "Archived & Relieved" section can render.
      // Count and main grid are filtered to active-only further below.
      const response = await axios.get(`${API_URL}/api/employees?include_archived=true`, { withCredentials: true });
      setEmployees(response.data);
    } catch (error) {
      console.error('Fetch employees error:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments`, { withCredentials: true });
      setDepartments(response.data);
    } catch (error) {
      console.error('Fetch departments error:', error);
    }
  };

  const fetchMasterTechStacks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tech-stacks`, { withCredentials: true });
      setMasterTechStacks(response.data);
    } catch (error) {
      console.error('Fetch tech stacks error:', error);
    }
  };

  const handleAddTechStack = async () => {
    if (!newTechName.trim()) { toast.error('Enter a technology name'); return; }
    try {
      await axios.post(`${API_URL}/api/tech-stacks`, {
        name: newTechName.trim(), category: newTechCategory.trim() || 'General',
      }, { withCredentials: true });
      toast.success(`${newTechName.trim()} added`);
      setNewTechName('');
      setNewTechCategory('');
      fetchMasterTechStacks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add technology');
    }
  };

  const handleDeleteTechStack = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from master list?`)) return;
    try {
      await axios.delete(`${API_URL}/api/tech-stacks/${id}`, { withCredentials: true });
      toast.success('Technology removed');
      fetchMasterTechStacks();
    } catch (err) {
      toast.error('Failed to remove technology');
    }
  };

  const openTechMapping = (emp, e) => {
    e.stopPropagation();
    setMappingEmployee(emp);
    setSelectedTechs(emp.tech_stack || []);
    setIsTechMappingOpen(true);
  };

  const handleSaveTechMapping = async () => {
    if (!mappingEmployee) return;
    try {
      await axios.put(`${API_URL}/api/employees/${mappingEmployee.id}/tech-stack`, {
        tech_stack: selectedTechs,
      }, { withCredentials: true });
      toast.success('Tech stack updated');
      setIsTechMappingOpen(false);
      setMappingEmployee(null);
      fetchEmployees();
    } catch (err) {
      toast.error('Failed to update tech stack');
    }
  };

  const toggleTech = (techName) => {
    setSelectedTechs(prev =>
      prev.includes(techName) ? prev.filter(t => t !== techName) : [...prev, techName]
    );
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!onboardingProof) {
      toast.error('Onboarding proof screenshot is required to generate the employee code');
      return;
    }
    if (newEmployee.create_login && (!newEmployee.initial_password || newEmployee.initial_password.length < 6)) {
      toast.error('Initial password must be at least 6 characters');
      return;
    }
    try {
      const payload = {
        name: newEmployee.name,
        email: newEmployee.email,
        phone: newEmployee.phone,
        personal_email: newEmployee.personal_email || null,
        department: newEmployee.department,
        designation: newEmployee.designation,
        years_of_experience: newEmployee.years_of_experience ? parseFloat(newEmployee.years_of_experience) : null,
        tech_stack: newEmployee.tech_stack ? newEmployee.tech_stack.split(',').map(s => s.trim()).filter(Boolean) : [],
        pilot_id: newEmployee.pilot_id || null,
        date_of_joining: newEmployee.date_of_joining || null,
        employee_id: (newEmployee.employee_id || '').trim().toUpperCase() || null,   // blank → server auto-generates
        employment_type: newEmployee.employment_type || 'full_time',
        contract_duration_months: ['contract','intern','fixed_term'].includes(newEmployee.employment_type) && newEmployee.contract_duration_months
          ? parseInt(newEmployee.contract_duration_months, 10)
          : null,
        contract_end_date: ['contract','intern','fixed_term'].includes(newEmployee.employment_type) && newEmployee.contract_end_date
          ? newEmployee.contract_end_date
          : null,
        team_kernel_id: newEmployee.team_kernel_id || null,
        team_kernel_layer_idx: newEmployee.team_kernel_layer_idx !== '' ? parseInt(newEmployee.team_kernel_layer_idx, 10) : null,
      };
      const form = new FormData();
      form.append('file', onboardingProof);
      form.append('payload', JSON.stringify(payload));
      const empRes = await axios.post(`${API_URL}/api/employees/onboard`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Optionally create login account
      if (newEmployee.create_login) {
        try {
          await axios.post(`${API_URL}/api/auth/admin-create-user`, {
            email: newEmployee.email,
            name: newEmployee.name,
            role: newEmployee.role,
            initial_password: newEmployee.initial_password,
            employee_id: empRes.data?.id || null,
          }, { withCredentials: true });
          toast.success(`Employee added · login created for ${newEmployee.email}. Share temp password with them — they must change it on first login.`);
        } catch (err) {
          toast.warning(`Employee added but login creation failed: ${err.response?.data?.detail || 'Unknown error'}`);
        }
      } else {
        toast.success('Employee added (no login account yet — admin can create one later).');
      }
      setIsAddDialogOpen(false);
      setNewEmployee({ name: '', email: '', phone: '', personal_email: '', department: '', designation: '', pilot_id: '', years_of_experience: '', date_of_joining: '', tech_stack: '', employee_id: '', employment_type: 'full_time', contract_duration_months: '12', contract_end_date: '', team_kernel_id: '', team_kernel_layer_idx: '', create_login: true, role: 'employee', initial_password: '' });
      setOnboardingProof(null);
      if (onboardingProofPreview) URL.revokeObjectURL(onboardingProofPreview);
      setOnboardingProofPreview(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    if (!editEmployee) return;
    try {
      await axios.put(`${API_URL}/api/employees/${editEmployee.id}`, {
        name: editEmployee.name,
        phone: editEmployee.phone,
        personal_email: editEmployee.personal_email || null,
        department: editEmployee.department,
        designation: editEmployee.designation,
        pilot_id: editEmployee.pilot_id || null,
        years_of_experience: editEmployee.years_of_experience ? parseFloat(editEmployee.years_of_experience) : null,
        date_of_joining: editEmployee.date_of_joining || null,
        employee_id: editEmployee.employee_id ? String(editEmployee.employee_id).trim().toUpperCase() : null,
        employment_type: editEmployee.employment_type || undefined,
        contract_duration_months: ['contract','intern','fixed_term'].includes(editEmployee.employment_type) && editEmployee.contract_duration_months
          ? parseInt(editEmployee.contract_duration_months, 10)
          : undefined,
        contract_end_date: ['contract','intern','fixed_term'].includes(editEmployee.employment_type) && editEmployee.contract_end_date
          ? editEmployee.contract_end_date
          : undefined,
        tech_stack: typeof editEmployee.tech_stack === 'string' ? editEmployee.tech_stack.split(',').map(s => s.trim()).filter(Boolean) : (editEmployee.tech_stack || []),
        attendance_exempt: !!editEmployee.attendance_exempt,
      }, { withCredentials: true });
      toast.success('Employee updated');
      setIsEditDialogOpen(false);
      setEditEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (id, name) => {
    if (!window.confirm(`Archive ${name}? They'll be moved to the archived list but data is preserved.`)) return;
    try {
      await axios.put(`${API_URL}/api/employees/${id}/archive`, {}, { withCredentials: true });
      toast.success('Employee archived');
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive employee');
    }
  };

  const handleRestoreEmployee = async (id, name) => {
    try {
      await axios.put(`${API_URL}/api/employees/${id}/restore`, {}, { withCredentials: true });
      toast.success(`${name} restored to active`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore employee');
    }
  };

  // Super-admin permanent delete — only from Archived & Relieved section
  const handlePermanentDelete = async (id, name) => {
    const confirm1 = window.confirm(`Permanently delete ${name}? This wipes their profile, payslips, attendance, salary structure, and login. THIS CANNOT BE UNDONE.`);
    if (!confirm1) return;
    const typed = window.prompt(`To confirm, type DELETE (in caps) to permanently remove ${name}.`);
    if (typed !== 'DELETE') { toast.info('Permanent delete cancelled'); return; }
    try {
      await axios.delete(`${API_URL}/api/employees/${id}?permanent=true`, { withCredentials: true });
      toast.success(`${name} permanently deleted`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete employee');
    }
  };

  const openOffboardDialog = (emp, e) => {
    e.stopPropagation();
    // Exit Management is now the single source of truth for offboarding (PIP → Resignation → Clearance → KT → F&F → Exit Interview).
    navigate(`/exit-management?employee_id=${emp.id}`);
  };

  const openEditDialog = (emp, e) => {
    e.stopPropagation();
    setEditEmployee({ ...emp });
    setIsEditDialogOpen(true);
    setEditProofFile(null);
    setEditProofMeta(null);
    // Fetch existing proof metadata so the button shows "Replace / Delete" vs "Upload"
    axios.get(`${API_URL}/api/employees/${emp.id}/onboarding-proof/meta`, { withCredentials: true })
      .then(res => setEditProofMeta(res.data?.available ? res.data : null))
      .catch(() => setEditProofMeta(null));
  };

  const uploadEditProof = async () => {
    if (!editProofFile || !editEmployee?.id) return;
    setEditProofBusy(true);
    try {
      const form = new FormData();
      form.append('file', editProofFile);
      const res = await axios.post(`${API_URL}/api/employees/${editEmployee.id}/onboarding-proof`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditProofMeta(res.data);
      setEditProofFile(null);
      toast.success('Onboarding proof updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload proof');
    } finally { setEditProofBusy(false); }
  };

  const viewEditProof = () => {
    if (!editEmployee?.id) return;
    window.open(`${API_URL}/api/employees/${editEmployee.id}/onboarding-proof`, '_blank');
  };

  const deleteEditProof = async () => {
    if (!editEmployee?.id) return;
    if (!window.confirm('Remove the onboarding proof for this employee?')) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${editEmployee.id}/onboarding-proof`, { withCredentials: true });
      setEditProofMeta(null);
      toast.success('Onboarding proof removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete proof');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.name?.toLowerCase().includes(search.toLowerCase()) ||
                         emp.email?.toLowerCase().includes(search.toLowerCase()) ||
                         emp.employee_id?.toLowerCase().includes(search.toLowerCase());
    const matchesDept = selectedDept === 'all' || emp.department === selectedDept;
    return matchesSearch && matchesDept && emp.status === 'active';
  });

  const archivedEmployees = employees.filter((emp) => ['archived', 'inactive', 'relieved'].includes(emp.status));

  const getPhotoUrl = (pic) => {
    if (!pic) return null;
    if (pic.startsWith('http')) return pic;
    return `${API_URL}/api/files/${pic}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Employee Directory</h1>
          <p className="text-muted-foreground" data-testid="employees-count">
            {employees.filter(e => e.status === 'active').length} active employee{employees.filter(e => e.status === 'active').length === 1 ? '' : 's'}
            {archivedEmployees.length > 0 && (
              <span className="ml-1 text-xs">· {archivedEmployees.length} archived/relieved</span>
            )}
          </p>
        </div>
        {isHR && (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setIsTechStackDialogOpen(true)} data-testid="manage-tech-stacks-btn">
              <Settings2 className="w-4 h-4" /> Tech Stacks
            </Button>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-employee-btn">
                  <Plus className="w-4 h-4" />
                  Add Employee
                </Button>
              </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
                <DialogDescription>Enter the employee details to add them to the system.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddEmployee} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={newEmployee.name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                    required
                    data-testid="new-employee-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                    required
                    data-testid="new-employee-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newEmployee.phone}
                    onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                    data-testid="new-employee-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="personal_email">Personal Email <span className="text-xs text-muted-foreground font-normal">(for offer letter, before Day 1)</span></Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={newEmployee.personal_email}
                    onChange={(e) => setNewEmployee({ ...newEmployee, personal_email: e.target.value })}
                    placeholder="candidate@gmail.com"
                    data-testid="new-employee-personal-email"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={newEmployee.department}
                      onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                      placeholder="e.g., Engineering"
                      data-testid="new-employee-department"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="designation">Designation</Label>
                    <Input
                      id="designation"
                      value={newEmployee.designation}
                      onChange={(e) => setNewEmployee({ ...newEmployee, designation: e.target.value })}
                      placeholder="e.g., ML Engineer"
                      data-testid="new-employee-designation"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pilot (Reporting Manager)</Label>
                    <Select value={newEmployee.pilot_id || "none"} onValueChange={(v) => setNewEmployee({ ...newEmployee, pilot_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="new-employee-pilot"><SelectValue placeholder="Select Pilot" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {employees.filter(e => e.status === 'active').map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation || e.department}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Years of Experience</Label>
                    <Input type="number" step="0.5" value={newEmployee.years_of_experience} onChange={(e) => setNewEmployee({ ...newEmployee, years_of_experience: e.target.value })} placeholder="e.g., 5" data-testid="new-employee-yoe" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date of Joining</Label>
                    <Input type="date" value={newEmployee.date_of_joining} onChange={(e) => setNewEmployee({ ...newEmployee, date_of_joining: e.target.value })} data-testid="new-employee-doj" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tech Stack (comma-separated)</Label>
                    <Input value={newEmployee.tech_stack} onChange={(e) => setNewEmployee({ ...newEmployee, tech_stack: e.target.value })} placeholder="Python, PyTorch, React" data-testid="new-employee-techstack" />
                  </div>
                </div>

                {/* Employee Code (WW###) — auto-generated by default; HR can override */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Employee Code <span className="text-[10px] text-muted-foreground">(auto-generated)</span></Label>
                    <Input
                      value={newEmployee.employee_id}
                      onChange={(e) => setNewEmployee({ ...newEmployee, employee_id: e.target.value.toUpperCase() })}
                      placeholder={nextCodeHint || (['contract','intern','fixed_term'].includes(newEmployee.employment_type) ? 'auto — e.g. WWFX001' : 'auto — e.g. WW001')}
                      data-testid="new-employee-code"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      {newEmployee.employee_id
                        ? <>Manual override: <b>{newEmployee.employee_id}</b></>
                        : nextCodeHint
                          ? <>Next auto code will be: <b>{nextCodeHint}</b>. Leave blank to accept, or type your own.</>
                          : <>Format: <b>WW###</b> full-time/part-time, <b>WWFX###</b> contract/intern.</>
                      }
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Employment Type</Label>
                    <Select value={newEmployee.employment_type} onValueChange={(v) => setNewEmployee({ ...newEmployee, employment_type: v })}>
                      <SelectTrigger data-testid="new-employee-emptype"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full-Time</SelectItem>
                        <SelectItem value="part_time">Part-Time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="intern">Intern</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {['contract','intern','fixed_term'].includes(newEmployee.employment_type) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{newEmployee.employment_type === 'intern' ? 'Internship Duration' : 'Contract Duration'}</Label>
                      <Select value={newEmployee.contract_duration_months} onValueChange={(v) => setNewEmployee({ ...newEmployee, contract_duration_months: v, contract_end_date: '' })}>
                        <SelectTrigger data-testid="new-employee-duration"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 months</SelectItem>
                          <SelectItem value="6">6 months</SelectItem>
                          <SelectItem value="9">9 months</SelectItem>
                          <SelectItem value="12">12 months (1 year)</SelectItem>
                          <SelectItem value="18">18 months</SelectItem>
                          <SelectItem value="24">24 months (2 years)</SelectItem>
                          <SelectItem value="36">36 months (3 years)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Contract End Date <span className="text-[10px] text-muted-foreground">(overrides duration)</span></Label>
                      <Input
                        type="date"
                        value={newEmployee.contract_end_date}
                        min={newEmployee.date_of_joining || undefined}
                        onChange={(e) => setNewEmployee({ ...newEmployee, contract_end_date: e.target.value })}
                        data-testid="new-employee-end-date"
                      />
                      <p className="text-[10px] text-muted-foreground">Auto-computed from DOJ + duration if left blank. After this date the employee is auto-archived at 03:00 IST.</p>
                    </div>
                  </div>
                )}

                {/* Team Kernel auto-placement */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Team Kernel (auto-add on create)</Label>
                    <Select value={newEmployee.team_kernel_id || "none"} onValueChange={(v) => setNewEmployee({ ...newEmployee, team_kernel_id: v === "none" ? "" : v, team_kernel_layer_idx: '' })}>
                      <SelectTrigger data-testid="new-employee-kernel"><SelectValue placeholder="Not added to any kernel" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not added to any kernel</SelectItem>
                        {teamKernels.map(k => (
                          <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {newEmployee.team_kernel_id && (() => {
                    const sel = teamKernels.find(k => k.id === newEmployee.team_kernel_id);
                    const layers = sel?.layers || [];
                    return (
                      <div className="space-y-2">
                        <Label>Layer (optional)</Label>
                        <Select value={newEmployee.team_kernel_layer_idx === '' ? "auto" : String(newEmployee.team_kernel_layer_idx)} onValueChange={(v) => setNewEmployee({ ...newEmployee, team_kernel_layer_idx: v === "auto" ? '' : v })}>
                          <SelectTrigger data-testid="new-employee-kernel-layer"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto (below manager, else last layer)</SelectItem>
                            {layers.map((l, i) => (
                              <SelectItem key={i} value={String(i)}>{i + 1}. {l.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })()}
                </div>

                {/* Onboarding proof — MANDATORY screenshot from the video call with the candidate */}
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="onboarding-proof-block">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 text-primary">📸</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">Onboarding proof <span className="text-destructive">*</span></p>
                      <p className="text-[11px] text-muted-foreground">
                        Upload a screenshot from your video call with the candidate. Required before the employee code can be generated. JPEG/PNG/WebP, max 8 MB.
                      </p>
                    </div>
                  </div>
                  <Input type="file" accept="image/*" onChange={onProofChange} data-testid="new-employee-proof-input" />
                  {onboardingProofPreview && (
                    <div className="mt-1">
                      <img src={onboardingProofPreview} alt="Onboarding proof preview" className="max-h-40 rounded border" />
                      <p className="text-[10px] text-muted-foreground mt-1">{onboardingProof?.name} · {(onboardingProof?.size / 1024).toFixed(0)} KB</p>
                    </div>
                  )}
                </div>

                {/* Login account fields */}
                <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
                  <div className="flex items-start gap-3">
                    <input id="create-login-toggle" type="checkbox" checked={newEmployee.create_login} onChange={(e) => setNewEmployee({ ...newEmployee, create_login: e.target.checked })} className="mt-1" data-testid="create-login-toggle" />
                    <label htmlFor="create-login-toggle" className="text-sm cursor-pointer">
                      <span className="font-semibold">Create login account</span>
                      <p className="text-xs text-muted-foreground">Adds a Wisewit portal account so the employee can sign in.</p>
                    </label>
                  </div>
                  {newEmployee.create_login && (
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="space-y-1.5">
                        <Label>Role</Label>
                        <Select value={newEmployee.role} onValueChange={(v) => setNewEmployee({ ...newEmployee, role: v })}>
                          <SelectTrigger data-testid="new-employee-role"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="manager">Manager / Pilot</SelectItem>
                            <SelectItem value="hr">HR</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Initial Password</Label>
                        <Input type="text" value={newEmployee.initial_password} onChange={(e) => setNewEmployee({ ...newEmployee, initial_password: e.target.value })} placeholder="Min 6 chars · share securely" minLength={6} data-testid="new-employee-initial-password" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="submit-new-employee">Add Employee</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="employee-search"
          />
        </div>
        <Select value={selectedDept} onValueChange={setSelectedDept}>
          <SelectTrigger className="w-[200px]" data-testid="department-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredEmployees.map((employee) => (
          <Card 
            key={employee.id} 
            className="cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => navigate(`/employees/${employee.id}`)}
            data-testid={`employee-card-${employee.employee_id}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={getPhotoUrl(employee.profile_picture)} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {getInitials(employee.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{employee.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">{employee.designation}</p>
                  <Badge variant="outline" className="mt-2 text-xs">
                    {employee.employee_id}
                  </Badge>
                </div>
                {user?.role === 'admin' && (
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Map Tech Stack" onClick={(e) => openTechMapping(employee, e)} data-testid={`tech-map-${employee.id}`}>
                      <Tag className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => openEditDialog(employee, e)} data-testid={`edit-emp-${employee.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700" title="Initiate Exit (Exit Management)" onClick={(e) => openOffboardDialog(employee, e)} data-testid={`offboard-emp-${employee.id}`}>
                      <UserMinus className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:text-amber-700" title="Archive" onClick={(e) => { e.stopPropagation(); handleDeleteEmployee(employee.id, employee.name); }} data-testid={`delete-emp-${employee.id}`}>
                      <Archive className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                {employee.department && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{employee.department}</span>
                  </div>
                )}
                {(() => {
                  const pilot = employees.find(e => e.id === employee.pilot_id);
                  return pilot ? (
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      <span>Pilot: <span className="font-medium text-foreground">{pilot.name}</span></span>
                    </div>
                  ) : null;
                })()}
                <div className="flex items-center gap-3 pt-1 border-t border-border/50 mt-2">
                  {employee.years_of_experience != null && (
                    <span className="text-xs"><Clock className="w-3 h-3 inline mr-1" />{employee.years_of_experience} YoE</span>
                  )}
                  {employee.date_of_joining && (
                    <span className="text-xs"><Calendar className="w-3 h-3 inline mr-1" />DoJ: {new Date(employee.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  )}
                </div>
                {employee.tech_stack?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1.5">
                    {employee.tech_stack.slice(0, 4).map((tech, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px] py-0 px-1.5 font-mono">{tech}</Badge>
                    ))}
                    {employee.tech_stack.length > 4 && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5">+{employee.tech_stack.length - 4}</Badge>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Employee Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee details.</DialogDescription>
          </DialogHeader>
          {editEmployee && (
            <form onSubmit={handleEditEmployee} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editEmployee.name || ''} onChange={(e) => setEditEmployee({ ...editEmployee, name: e.target.value })} required data-testid="edit-employee-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={editEmployee.email || ''} disabled className="opacity-60" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editEmployee.phone || ''} onChange={(e) => setEditEmployee({ ...editEmployee, phone: e.target.value })} data-testid="edit-employee-phone" />
              </div>
              <div className="space-y-2">
                <Label>Personal Email <span className="text-xs text-muted-foreground font-normal">(for offer letter)</span></Label>
                <Input type="email" value={editEmployee.personal_email || ''} onChange={(e) => setEditEmployee({ ...editEmployee, personal_email: e.target.value })} placeholder="candidate@gmail.com" data-testid="edit-employee-personal-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input value={editEmployee.department || ''} onChange={(e) => setEditEmployee({ ...editEmployee, department: e.target.value })} data-testid="edit-employee-department" />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input value={editEmployee.designation || ''} onChange={(e) => setEditEmployee({ ...editEmployee, designation: e.target.value })} data-testid="edit-employee-designation" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pilot (Reporting Manager)</Label>
                  <Select value={editEmployee.pilot_id || "none"} onValueChange={(v) => setEditEmployee({ ...editEmployee, pilot_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-employee-pilot"><SelectValue placeholder="Select Pilot" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {employees.filter(e => e.status === 'active' && e.id !== editEmployee.id).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.name} — {e.designation || e.department}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input type="number" step="0.5" value={editEmployee.years_of_experience || ''} onChange={(e) => setEditEmployee({ ...editEmployee, years_of_experience: e.target.value })} data-testid="edit-employee-yoe" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Joining</Label>
                  <Input type="date" value={editEmployee.date_of_joining || ''} onChange={(e) => setEditEmployee({ ...editEmployee, date_of_joining: e.target.value })} data-testid="edit-employee-doj" />
                </div>
                <div className="space-y-2">
                  <Label>Tech Stack</Label>
                  <Input value={Array.isArray(editEmployee.tech_stack) ? editEmployee.tech_stack.join(', ') : (editEmployee.tech_stack || '')} onChange={(e) => setEditEmployee({ ...editEmployee, tech_stack: e.target.value })} placeholder="Python, PyTorch" data-testid="edit-employee-techstack" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Code</Label>
                  <Input value={editEmployee.employee_id || ''} onChange={(e) => setEditEmployee({ ...editEmployee, employee_id: e.target.value.toUpperCase() })} placeholder={['contract','intern','fixed_term'].includes(editEmployee.employment_type) ? 'WWFX001' : 'WW001'} data-testid="edit-employee-code" />
                </div>
                <div className="space-y-2">
                  <Label>Employment Type</Label>
                  <Select value={editEmployee.employment_type || 'full_time'} onValueChange={(v) => setEditEmployee({ ...editEmployee, employment_type: v })}>
                    <SelectTrigger data-testid="edit-employee-emptype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Full-Time</SelectItem>
                      <SelectItem value="part_time">Part-Time</SelectItem>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="intern">Intern</SelectItem>
                      {editEmployee.employment_type === 'fixed_term' && (
                        <SelectItem value="fixed_term">Fixed-Term (legacy)</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {['contract','intern','fixed_term'].includes(editEmployee.employment_type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{editEmployee.employment_type === 'intern' ? 'Internship Duration' : 'Contract Duration'}</Label>
                    <Select value={String(editEmployee.contract_duration_months || '12')} onValueChange={(v) => setEditEmployee({ ...editEmployee, contract_duration_months: v })}>
                      <SelectTrigger data-testid="edit-employee-duration"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 months</SelectItem>
                        <SelectItem value="6">6 months</SelectItem>
                        <SelectItem value="9">9 months</SelectItem>
                        <SelectItem value="12">12 months (1 year)</SelectItem>
                        <SelectItem value="18">18 months</SelectItem>
                        <SelectItem value="24">24 months (2 years)</SelectItem>
                        <SelectItem value="36">36 months (3 years)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {editEmployee.contract_end_date !== undefined && (
                    <div className="space-y-2">
                      <Label>Contract End Date</Label>
                      <Input
                        type="date"
                        value={editEmployee.contract_end_date || ''}
                        min={editEmployee.date_of_joining || undefined}
                        onChange={(e) => setEditEmployee({ ...editEmployee, contract_end_date: e.target.value })}
                        data-testid="edit-employee-end-date"
                      />
                      <p className="text-[10px] text-muted-foreground">Overrides duration. After this date the employee is auto-archived at 03:00 IST.</p>
                    </div>
                  )}
                </div>
              )}
              {/* Founder / attendance-exempt */}
              <div className="flex items-center justify-between rounded-lg border p-3" data-testid="edit-attendance-exempt-block">
                <div className="pr-4">
                  <Label className="text-sm font-semibold">Founder / Attendance-exempt</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">When on, this person is never marked absent and has no attendance-based salary deductions (LOP).</p>
                </div>
                <Switch
                  checked={!!editEmployee.attendance_exempt}
                  onCheckedChange={(v) => setEditEmployee({ ...editEmployee, attendance_exempt: v })}
                  data-testid="edit-attendance-exempt-switch"
                />
              </div>
              {/* Onboarding proof management */}
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="edit-proof-block">
                <p className="text-sm font-semibold flex items-center gap-2">📸 Onboarding Proof</p>
                {editProofMeta ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Current: <b>{editProofMeta.filename || 'image'}</b>
                      {editProofMeta.uploaded_at && <> · uploaded {new Date(editProofMeta.uploaded_at).toLocaleDateString('en-IN')}</>}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={viewEditProof} data-testid="edit-proof-view">View</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={deleteEditProof} data-testid="edit-proof-delete">Delete</Button>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Input type="file" accept="image/*" onChange={(e) => setEditProofFile(e.target.files?.[0] || null)} data-testid="edit-proof-file" />
                      <Button type="button" size="sm" disabled={!editProofFile || editProofBusy} onClick={uploadEditProof} data-testid="edit-proof-replace">Replace</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/*" onChange={(e) => setEditProofFile(e.target.files?.[0] || null)} data-testid="edit-proof-file" />
                    <Button type="button" size="sm" disabled={!editProofFile || editProofBusy} onClick={uploadEditProof} data-testid="edit-proof-upload">Upload</Button>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-employee-edit">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>No employees found matching your criteria.</p>
        </div>
      )}

      {/* Archived / Relieved Employees */}
      {isHR && archivedEmployees.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Archive className="w-4 h-4" /> Archived & Relieved Employees ({archivedEmployees.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {archivedEmployees.map(emp => (
                <div key={emp.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30" data-testid={`archived-${emp.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">{getInitials(emp.name)}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{emp.name}</p>
                        {emp.status === 'relieved' && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-rose-300 text-rose-700 dark:text-rose-300">RELIEVED</Badge>
                        )}
                        {emp.status === 'archived' && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 dark:text-amber-300">ARCHIVED</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{emp.email} | {emp.employee_id} | {emp.designation || emp.department || ''}</p>
                      {emp.status === 'relieved' && emp.relieving_date && (
                        <p className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                          Last day: {new Date(emp.relieving_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {emp.exit_reason ? ` · ${emp.exit_reason}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => handleRestoreEmployee(emp.id, emp.name)} data-testid={`restore-${emp.id}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </Button>
                    {user?.role === 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handlePermanentDelete(emp.id, emp.name)}
                        title="Permanently delete (admin only, cannot be undone)"
                        data-testid={`hard-delete-${emp.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete forever
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manage Tech Stacks Dialog */}
      <Dialog open={isTechStackDialogOpen} onOpenChange={setIsTechStackDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5" /> Manage Tech Stacks</DialogTitle>
            <DialogDescription>Add or remove technologies from the master list. Map them to employees via the tag icon on each card.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={newTechName} onChange={(e) => setNewTechName(e.target.value)} placeholder="Technology name (e.g., Python)" className="flex-1" data-testid="new-tech-name" />
              <Input value={newTechCategory} onChange={(e) => setNewTechCategory(e.target.value)} placeholder="Category" className="w-32" data-testid="new-tech-category" />
              <Button size="sm" onClick={handleAddTechStack} data-testid="add-tech-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {masterTechStacks.length > 0 ? masterTechStacks.map(tech => (
                <div key={tech.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-xs">{tech.name}</Badge>
                    <span className="text-xs text-muted-foreground">{tech.category}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteTechStack(tech.id, tech.name)} data-testid={`delete-tech-${tech.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )) : <p className="text-center text-muted-foreground text-sm py-4">No technologies added yet</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tech Stack Mapping Dialog */}
      <Dialog open={isTechMappingOpen} onOpenChange={setIsTechMappingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Tag className="w-5 h-5" /> Map Tech Stack — {mappingEmployee?.name}</DialogTitle>
            <DialogDescription>Select technologies to assign to this employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {masterTechStacks.length > 0 ? (
              <div className="max-h-60 overflow-y-auto space-y-1">
                {masterTechStacks.map(tech => {
                  const isSelected = selectedTechs.includes(tech.name);
                  return (
                    <div
                      key={tech.id}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 border border-primary/30' : 'bg-muted/30 hover:bg-muted/50'}`}
                      onClick={() => toggleTech(tech.name)}
                      data-testid={`tech-option-${tech.name}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                        {isSelected && <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <span className="text-sm font-mono">{tech.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{tech.category}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground text-sm py-4">No technologies in master list. Add some via "Tech Stacks" button first.</p>
            )}
            {selectedTechs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                {selectedTechs.map(t => (
                  <Badge key={t} className="gap-1 font-mono text-xs" onClick={() => toggleTech(t)}>
                    {t} <X className="w-3 h-3 cursor-pointer" />
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsTechMappingOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTechMapping} data-testid="save-tech-mapping-btn">Save Mapping</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default EmployeesPage;
